import type { FC } from 'react';
import React, { useRef } from 'react';
import { Box } from '@withjoy/joykit';
import type { CroppedImageProps } from './CroppedImage.types';
import { useDrawCroppedImageToCanvas } from '@shared/hooks/useDrawCroppedImageToCanvas';

export const CroppedImage: FC<CroppedImageProps> = props => {
  const { imageUrl, cropInfo, width, height, canvasStyles = {}, useCSS = false } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const { hasError } = useDrawCroppedImageToCanvas({ canvasRef, cropInfo, height, width, imageUrl, skip: useCSS });

  // CSS path: replicate canvas ctx.drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh)
  // using an <img> inside an overflow:hidden container.
  //
  // The crop info is in percentages (0-100) of the original image:
  //   x, y      = top-left corner of the crop region
  //   cropWidth  = width of the crop region (e.g. 65 means 65% of the image)
  //   cropHeight = height of the crop region
  //
  // To show only the crop region inside the container:
  //
  //   WIDTH:  The crop is cropWidth% of the full image. To make the crop fill
  //           100% of the container, the full image must be (100 / cropWidth)
  //           times the container width. In CSS percentage: 100 * (100 / cropWidth)
  //           = 10000 / cropWidth. e.g. cropWidth=65 → image is 153.8% of container.
  //
  //   HEIGHT: Same logic — 10000 / cropHeight.
  //
  //   LEFT:   The crop starts at x% of the full image. At the scaled width,
  //           that's (x / 100) * imageWidth = (x / 100) * (10000 / cropWidth)%
  //           of container = (x / cropWidth * 100)% of container. Negate to
  //           shift left: -(x / cropWidth) * 100%.
  //
  //   TOP:    Same logic — -(y / cropHeight) * 100%.
  //
  // The container clips the overflow, showing only the crop region.
  if (useCSS) {
    const { x, y, width: cropWidth, height: cropHeight } = cropInfo;
    return (
      <Box
        width="100%"
        height="100%"
        overflow="hidden"
        position="relative"
      >
        <Box
          as="img"
          src={imageUrl}
          alt=""
          position="absolute"
          style={{
            width: `${10000 / cropWidth}%`,
            height: `${10000 / cropHeight}%`,
            left: `${-(x / cropWidth) * 100}%`,
            top: `${-(y / cropHeight) * 100}%`,
          }}
        />
      </Box>
    );
  }

  // Fallback to background image if there's an error
  if (hasError) {
    return (
      <Box
        width={width}
        height={height}
        overflow="hidden"
        background={`url("${imageUrl}") lightgray 50%`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
      />
    );
  }

  return (
    <Box
      as="canvas"
      ref={canvasRef}
      width={width}
      height={height}
      overflow="hidden"
      display="block"
      background="lightgray"
      {...canvasStyles}
    />
  );
};

CroppedImage.displayName = 'CroppedImage';
